/**
 * Analytics › Overview: is anyone watching. The KPI tiles (the vertical's headline, plays, watch
 * time, viewers, average watch, completion) against the previous period, the day-by-day chart with
 * the previous period behind it, and the most played items.
 */

import Button from "@web-ui/components/Button";
import { SkeletonCard } from "@web-ui/components/Skeleton";
import { href } from "@/lib/route";
import { analytics, type ContentRow } from "../api";
import type { Table } from "../csv";
import { definition, kpiTiles, type MetricId } from "../definitions";
import { count, duration } from "../format";
import { KpiRow } from "../components/kpi-row";
import { NoData, ReportFailed } from "../components/no-data";
import { ReportCard } from "../components/report-card";
import { SampleNotice } from "../components/sample-notice";
import { TopContent } from "../components/top-content";
import { TrendCard } from "../components/trend-card";
import type { ReportScope } from "../scope";
import { useKpis } from "../use-kpis";
import { useReport } from "../use-report";

export function Overview({ scope }: { scope: ReportScope }) {
  const ids = [...new Set<MetricId>([...kpiTiles(scope.pack), "averageWatch", "completionRate"])].slice(0, 6);
  const kpis = useKpis(scope, { ids, previousSeries: true });
  const content = useReport(() => analytics.content(scope.client, scope.range), [scope.client, scope.range]);
  const s = kpis.summary.data;

  if (kpis.summary.failed && !s) return <section className="card"><ReportFailed /></section>;
  const stale = kpis.summary.stale || content.stale;

  return (
    <div className={`flex flex-col gap-3 transition-opacity ${stale ? "opacity-60" : ""}`} aria-busy={stale || undefined}>
      <SampleNotice shown={kpis.sample || !!content.data?.sample} />
      <KpiRow tiles={kpis.tiles} placeholders={ids.length} columns={3} />
      {!s ? <SkeletonCard label="Loading the chart" rows={5} /> : (
        <TrendCard scope={scope} current={s.series} before={kpis.previous.data?.series ?? null} what="overview" empty={kpis.empty || s.totals.plays === 0} />
      )}
      <TopContentCard scope={scope} rows={content.data?.items ?? null} />
    </div>
  );
}

function TopContentCard({ scope, rows }: { scope: ReportScope; rows: ContentRow[] | null }) {
  const plays = definition("plays", scope.pack);
  const table: Table<ContentRow> = {
    columns: [
      { label: "Name", value: (r) => scope.nameOf(r.itemId).label, href: (r) => scope.nameOf(r.itemId).href },
      { label: "Item id", value: (r) => r.itemId, csvOnly: true },
      { label: plays.label, value: (r) => r.plays, format: (v) => count(Number(v)), numeric: true },
      { label: "Watch time", csvLabel: "Watch time (seconds)", value: (r) => r.watchSeconds, format: (v) => duration(Number(v)), numeric: true },
    ],
    rows: (rows ?? []).slice(0, 5),
  };
  if (!rows) return <SkeletonCard label="Loading the most played" rows={5} />;
  return (
    <ReportCard title={`Most played ${scope.pack.nouns.title.many.toLowerCase()}`} description="The five with the most plays in this period." table={table}
      file={{ brand: scope.brand, what: "most played", range: scope.range }}
      empty={rows.length === 0 ? <NoData /> : undefined}
      actions={<Button as="a" variant="ghost" size="sm" href={href({ brand: scope.brand, section: "analytics", report: "content", range: scope.days })}>See all</Button>}>
      <TopContent rows={rows} nameOf={scope.nameOf} />
    </ReportCard>
  );
}
